import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { cn } from "../lib/utils"
import { IMAGE_SIZE, OBJECT_FIT } from "../lib/style-resolver"

export function ImageRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const atomDefaults = useAtomDefaults("image")
  const resolvedVariant = (element as { variant?: string }).variant
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const src = String(resolveSymbol(element.src ?? "") ?? "")
  const alt = String(resolveSymbol(element.alt ?? "") ?? "")
  const dataAttrs = {
    "data-atom-type": "image",
    "data-variant": resolvedVariant,
    "data-size": resolvedSize,
  }

  const sizeClasses = element.size ? IMAGE_SIZE[element.size] ?? "" : ""
  const hasExplicitSize = !!sizeClasses

  // Resolve styling props
  const styling = element.styling as Record<string, unknown> | undefined
  const inlineStyle: React.CSSProperties = {}
  if (styling?.width) inlineStyle.width = typeof styling.width === "number" ? `${styling.width}px` : String(styling.width)
  if (styling?.height) inlineStyle.height = typeof styling.height === "number" ? `${styling.height}px` : String(styling.height)
  const objectFit = styling?.objectFit as string | undefined
  const aspectRatioRaw = styling?.aspectRatio ?? element.aspectRatio
  if (aspectRatioRaw) {
    const ar = String(aspectRatioRaw)
    inlineStyle.aspectRatio = ar.replace(":", "/")
  }
  const hasStyle = Object.keys(inlineStyle).length > 0

  // Detect sizing/aspect classes in className that require a wrapper div.
  // Without a wrapper, <img> ignores Tailwind size constraints and renders at
  // its intrinsic (original) dimensions, causing layout overflow.
  const cls = element.className ?? ""
  const hasSizingClass = /\b(size-|aspect-|w-\d|h-\d|max-w-|max-h-)/.test(cls)
  const needsWrapper = hasExplicitSize || aspectRatioRaw || element.rounded || hasSizingClass

  if (!needsWrapper) {
    if (!src) return null
    return (
      <img
        src={src}
        alt={alt}
        className={cn(
          objectFit ? OBJECT_FIT[objectFit] ?? "" : undefined,
          element.className,
        )}
        style={hasStyle ? inlineStyle : undefined}
        {...dataAttrs}
      />
    )
  }

  return (
    <div
      {...dataAttrs}
      className={cn(
        "overflow-hidden",
        sizeClasses,
        !hasExplicitSize && (aspectRatioRaw === "1:1" || aspectRatioRaw === "square") && "aspect-square",
        (element.fit || objectFit) && "bg-muted",
        element.rounded && `rounded-${element.rounded}`,
        element.className,
      )}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          className={cn(
            "h-full w-full",
            objectFit ? OBJECT_FIT[objectFit] ?? "object-cover" : element.fit ? OBJECT_FIT[element.fit] ?? "object-cover" : undefined,
          )}
          style={hasStyle ? inlineStyle : undefined}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-muted/50">
          <svg className="h-8 w-8 text-muted-foreground/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
          </svg>
        </div>
      )}
    </div>
  )
}
